import { EventType, Importance, SignalTag } from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { isOneOf } from '@blisspointmedia/bpm-types/dist/typeGuards';

// Frontend-owned, not CategoryTags: the wire category_tags holds only the signal family.
export const EventThemes = [
  'Relationship',
  'Budget & Pacing',
  'Creative',
  'Goals & Benchmarks',
  'Campaign Launches & Changes',
  'Campaign Performance',
  'Tasks',
  'Timelines',
  'Updates and Decisions',
  'Meetings & Admin',
  'Business Performance',
  'Other'
] as const;
export type EventTheme = (typeof EventThemes)[number];
export const isEventTheme = isOneOf(EventThemes);

export interface EventTaxonomyEntry {
  theme: EventTheme;
  signalTag: SignalTag;
  /** 1–5 risk/opportunity weight; null for informational events with no signal. */
  weight: number | null;
}

export const EventTaxonomy: Record<EventType, EventTaxonomyEntry> = {
  'Upcoming Contract Renewal Events': {
    theme: 'Relationship',
    signalTag: SignalTag.OPPORTUNITIES,
    weight: 3
  },
  'New stakeholder the client side': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 3
  },
  'Campaign Launches': {
    theme: 'Campaign Launches & Changes',
    signalTag: SignalTag.NONE,
    weight: null
  },
  'Campaign Changes': {
    theme: 'Campaign Launches & Changes',
    signalTag: SignalTag.NONE,
    weight: null
  },
  'Budget Changes: Net Neutral or Re-Allocation within Channels': {
    theme: 'Budget & Pacing',
    signalTag: SignalTag.NONE,
    weight: null
  },
  'Budget Changes: Increase': {
    theme: 'Budget & Pacing',
    signalTag: SignalTag.OPPORTUNITIES,
    weight: 2
  },
  'Budget Changes: Decrease': {
    theme: 'Budget & Pacing',
    signalTag: SignalTag.PERFORMANCE_BUSINESS_RISK,
    weight: 3
  },
  'JBP/Joint Business Plan Changes': {
    theme: 'Goals & Benchmarks',
    signalTag: SignalTag.NONE,
    weight: null
  },
  'Pacing: On Target': {
    theme: 'Budget & Pacing',
    signalTag: SignalTag.NONE,
    weight: null
  },
  'Pacing: Below Target': {
    theme: 'Budget & Pacing',
    signalTag: SignalTag.PERFORMANCE_BUSINESS_RISK,
    weight: 2
  },
  'Pacing: Over Target': {
    theme: 'Budget & Pacing',
    signalTag: SignalTag.PERFORMANCE_BUSINESS_RISK,
    weight: 2
  },
  'Pacing: Repeated Pacing Issues': {
    theme: 'Budget & Pacing',
    signalTag: SignalTag.PERFORMANCE_BUSINESS_RISK,
    weight: 3
  },
  'Negative Performance': {
    theme: 'Campaign Performance',
    signalTag: SignalTag.PERFORMANCE_BUSINESS_RISK,
    weight: 3
  },
  'Positive Performance': {
    theme: 'Campaign Performance',
    signalTag: SignalTag.OPPORTUNITIES,
    weight: 1
  },
  'Changes in Performance: Consistent Negative': {
    theme: 'Campaign Performance',
    signalTag: SignalTag.PERFORMANCE_BUSINESS_RISK,
    weight: 4
  },
  'Test Results: Neutral': {
    theme: 'Campaign Performance',
    signalTag: SignalTag.NONE,
    weight: null
  },
  'Test Results: Negative': {
    theme: 'Campaign Performance',
    signalTag: SignalTag.PERFORMANCE_BUSINESS_RISK,
    weight: 2
  },
  'Test Results: Positive': {
    theme: 'Campaign Performance',
    signalTag: SignalTag.OPPORTUNITIES,
    weight: 2
  },
  'Upcoming Out of Office': {
    theme: 'Meetings & Admin',
    signalTag: SignalTag.NONE,
    weight: null
  },
  'Upcoming Meeting': {
    theme: 'Meetings & Admin',
    signalTag: SignalTag.NONE,
    weight: null
  },
  'Past Meeting': { theme: 'Meetings & Admin', signalTag: SignalTag.NONE, weight: null },
  'Follow Up Sent': { theme: 'Meetings & Admin', signalTag: SignalTag.NONE, weight: null },
  'Business Performance: Positive': {
    theme: 'Business Performance',
    signalTag: SignalTag.OPPORTUNITIES,
    weight: 3
  },
  'Business Performance: Negative': {
    theme: 'Business Performance',
    signalTag: SignalTag.PERFORMANCE_BUSINESS_RISK,
    weight: 4
  },
  'Campaign Goal: Repeated Misses': {
    theme: 'Campaign Performance',
    signalTag: SignalTag.PERFORMANCE_BUSINESS_RISK,
    weight: 3
  },
  'Mistake: QC or Error Found by Client': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 5
  },
  'Mistake: QC or Error Caught by Our Team': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 2
  },
  'Change in Personnel on Client Side': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 3
  },
  'Change in Personnel on Tinuiti Side': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 2
  },
  'Change in Communication Patterns': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 4
  },
  'Change in Frequency of Requests': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 3
  },
  'Recurring Cancelled Calls': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 3
  },
  'Vibe and Sentiment from Client - Negative': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 5
  },
  'Vibe and Sentiment from Client - Positive': {
    theme: 'Relationship',
    signalTag: SignalTag.OPPORTUNITIES,
    weight: 2
  },
  'Persistent Disagreement from Client': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 5
  },
  'Corporate Restructuring': {
    theme: 'Business Performance',
    signalTag: SignalTag.PERFORMANCE_BUSINESS_RISK,
    weight: 5
  },
  'Industry Headwinds: General': {
    theme: 'Business Performance',
    signalTag: SignalTag.PERFORMANCE_BUSINESS_RISK,
    weight: 3
  },
  'Industry Headwinds: Legal': {
    theme: 'Business Performance',
    signalTag: SignalTag.PERFORMANCE_BUSINESS_RISK,
    weight: 5
  },
  'Consistent Follow Ups': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 3
  },
  'Feedback of Lack of Strategy, Limited Rationale': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 5
  },
  'Shift to Focus on Minutiae or Transactional Conversations, Lack of Strategic Guidance and Collaboration':
    { theme: 'Relationship', signalTag: SignalTag.RELATIONSHIP_RISK, weight: 4 },
  'PE or VC Scrutiny': {
    theme: 'Business Performance',
    signalTag: SignalTag.PERFORMANCE_BUSINESS_RISK,
    weight: 5
  },
  'PE or VC Funding': {
    theme: 'Business Performance',
    signalTag: SignalTag.OPPORTUNITIES,
    weight: 5
  },
  'Talent Gaps': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 4
  },
  Layoffs: {
    theme: 'Business Performance',
    signalTag: SignalTag.PERFORMANCE_BUSINESS_RISK,
    weight: 5
  },
  'Hiring Freezes': {
    theme: 'Business Performance',
    signalTag: SignalTag.PERFORMANCE_BUSINESS_RISK,
    weight: 3
  },
  'Siloed Storytelling': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 3
  },
  'Inquiring about Other Tinuiti Services, Channels, or Measurement': {
    theme: 'Relationship',
    signalTag: SignalTag.OPPORTUNITIES,
    weight: 4
  },
  'RFP of Our Services': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 5
  },
  'Unusual or Unprompted Lines of Questioning': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 4
  },
  'RFP Outside of Scoped Services': {
    theme: 'Relationship',
    signalTag: SignalTag.OPPORTUNITIES,
    weight: 5
  },
  'Reductions in Scope': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 5
  },
  'In-Housing': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 5
  },
  Consolidation: {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 5
  },
  'Unhappiness with Other Agency Partners': {
    theme: 'Other',
    signalTag: SignalTag.OPPORTUNITIES,
    weight: 2
  },
  Task: {
    theme: 'Tasks',
    signalTag: SignalTag.NONE,
    weight: null
  },
  'Response to Request or Deliverable Shared': {
    theme: 'Updates and Decisions',
    signalTag: SignalTag.NONE,
    weight: null
  },
  'Creative Expiration or Rights Date': {
    theme: 'Creative',
    signalTag: SignalTag.NONE,
    weight: null
  },
  Approval: { theme: 'Updates and Decisions', signalTag: SignalTag.NONE, weight: null },
  'Changes in Goals or Strategy': {
    theme: 'Goals & Benchmarks',
    signalTag: SignalTag.NONE,
    weight: null
  },
  'Changes in Timeline': {
    theme: 'Timelines',
    signalTag: SignalTag.NONE,
    weight: null
  },
  'Expansion of Business': {
    theme: 'Business Performance',
    signalTag: SignalTag.OPPORTUNITIES,
    weight: 2
  },
  'Brand Expansion': {
    theme: 'Business Performance',
    signalTag: SignalTag.OPPORTUNITIES,
    weight: 3
  },
  'Audit opportunities': { theme: 'Other', signalTag: SignalTag.OPPORTUNITIES, weight: 4 },
  'Executive Sponsor Disengagement': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 5
  },
  'Stakeholder Attendance Declines': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 4
  },
  'Procurement Becomes More Involved, New Procurement or Vendor Review Process': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 5
  },
  'Delayed Approvals': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 3
  },
  'Delayed Payment / Billing Issues': {
    theme: 'Business Performance',
    signalTag: SignalTag.PERFORMANCE_BUSINESS_RISK,
    weight: 4
  },
  'Client Stops Sharing Business Context': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 4
  },
  'Requests for Increased Documentation': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 4
  },
  'Requests for Knowledge Transfer': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 5
  },
  'Requests for Data Exports or Asset Ownership': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 5
  },
  'Increased Competitive Benchmarking': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 4
  },
  'Client References Other Agency Capabilities Frequently': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 4
  },
  'Increase in Executive Escalations': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 5
  },
  'Decisions Delayed or Deferred, Multiple Missed Deadlines by Client': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 3
  },
  'Increased Price Sensitivity': {
    theme: 'Business Performance',
    signalTag: SignalTag.PERFORMANCE_BUSINESS_RISK,
    weight: 4
  },
  'Questions About Contract Terms or Termination Clauses': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 5
  },
  'Increased Requests for Tactical Work vs. Strategic Partnership': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 3
  },
  'Declining Adoption of Agency Recommendations': {
    theme: 'Relationship',
    signalTag: SignalTag.RELATIONSHIP_RISK,
    weight: 4
  },
  Other: { theme: 'Other', signalTag: SignalTag.NONE, weight: null }
};

export interface DerivedEventFields {
  theme: EventTheme;
  signalTag: SignalTag;
  weight: number | null;
  importance: Importance;
}
